import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { Gamepad2, Loader2, LogOut, Maximize, Minimize, RotateCw, ShieldCheck, Volume2, VolumeX, Wifi } from "lucide-react";
import api from "@/lib/api";
import { BrandLogo } from "@/components/BrandLogo";
import { useCloudStream } from "@/components/play/useCloudStream";
import { useStreamInput } from "@/components/play/useStreamInput";
import { VirtualPad } from "@/components/play/VirtualPad";
import { ControlsCard } from "@/components/play/ControlsCard";

const isTouch = typeof window !== "undefined" && ("ontouchstart" in window || navigator.maxTouchPoints > 0);

function Signal({ fps, rtt }) {
  const level = rtt < 120 && (fps === 0 || fps >= 8) ? "good" : rtt < 300 ? "ok" : "poor";
  const cls = { good: "text-emerald-400", ok: "text-amber-400", poor: "text-red-400" }[level];
  return (
    <span data-testid="play-signal" title={`${fps} fps · ${rtt} ms`} className={`flex items-center gap-1.5 text-xs font-mono ${cls}`}>
      <Wifi className="w-4 h-4" /> <span className="hidden sm:inline">{fps} fps · {rtt} ms</span>
    </span>
  );
}

function SoundControl({ audio }) {
  const [muted, setMuted] = useState(audio.muted);
  const [vol, setVol] = useState(audio.volume);
  const toggle = () => { audio.unlock(); audio.muted = !muted; audio.apply(); setMuted(!muted); };
  const change = (v) => { audio.unlock(); audio.volume = v; audio.muted = false; audio.apply(); setVol(v); setMuted(false); };
  return (
    <div className="flex items-center gap-1" data-play-ui>
      <button data-testid="play-sound" onClick={toggle} aria-pressed={!muted} title={muted ? "Unmute" : "Mute"}
        className={`w-9 h-9 rounded-lg border flex items-center justify-center ${muted ? "border-zinc-800 text-zinc-500" : "border-volt text-volt"}`}>
        {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
      </button>
      <input data-testid="play-volume" type="range" min="0" max="1" step="0.05" value={muted ? 0 : vol} onChange={(e) => change(Number(e.target.value))}
        aria-label="Volume" className="hidden md:block w-20 accent-[#0EA5E9]" />
    </div>
  );
}

function Center({ children }) {
  return <div className="absolute inset-0 flex flex-col items-center justify-start text-center p-6 gap-4 overflow-y-auto [&>*:first-child]:mt-auto [&>*:last-child]:mb-auto">{children}</div>;
}

export default function Play() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [err, setErr] = useState("");
  const [fs, setFs] = useState(false);
  const [pad, setPad] = useState(isTouch);
  const [q, setQ] = useState(isTouch ? "540" : "720");
  const stageRef = useRef(null);
  const { phase, info, quality, canvasRef, send, start, exit, hasKey, audio } = useCloudStream(token, api);
  useStreamInput(canvasRef, send, phase === "playing");

  useEffect(() => {
    api.get(`/cloud/play/${token}`).then((r) => setMeta(r.data)).catch((e) => setErr(e.response?.data?.detail || "This game link is invalid."));
    const onFs = () => setFs(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFs);
    return () => document.removeEventListener("fullscreenchange", onFs);
  }, [token]);

  useEffect(() => { if (meta && hasKey && meta.session_state && meta.session_state !== "ended" && phase === "idle") start(q); }, [meta, hasKey, phase, start, q]);

  const toggleFs = async () => {
    try {
      if (document.fullscreenElement) await document.exitFullscreen();
      else { await stageRef.current.requestFullscreen({ navigationUI: "hide" }); try { await window.screen.orientation?.lock?.("landscape"); } catch { /* not supported */ } }
    } catch { /* fullscreen refused */ }
  };

  const playing = phase === "playing" || phase === "reconnecting";
  return (
    <div className="h-[100dvh] bg-black text-white flex flex-col force-dark" data-testid="play-page">
      <header data-play-ui className={`h-14 border-b border-zinc-800 flex items-center justify-between gap-3 px-3 sm:px-5 shrink-0 ${fs ? "hidden" : ""}`}>
        <div className="flex items-center gap-2 min-w-0">
          <BrandLogo className="w-6 h-6 shrink-0" />
          <div className="min-w-0">
            <div className="font-head font-semibold text-sm truncate" data-testid="play-title">{meta?.title || "Crank.live Cloud Play"}</div>
            <div className="text-[11px] text-zinc-500 flex items-center gap-1 whitespace-nowrap min-w-0"><ShieldCheck className="w-3 h-3 text-volt shrink-0" /> <span className="truncate">Protected browser · {meta?.host}</span></div>
          </div>
        </div>
        {playing && (
          <div className="flex items-center gap-1.5 sm:gap-2 shrink-0">
            <Signal {...quality} />
            <SoundControl audio={audio} />
            <button data-testid="play-pad-toggle" onClick={() => setPad((p) => !p)} aria-pressed={pad} title="On-screen controls" className={`w-9 h-9 rounded-lg border flex items-center justify-center ${pad ? "border-volt text-volt" : "border-zinc-800 text-zinc-400 hover:text-white"}`}><Gamepad2 className="w-4 h-4" /></button>
            <button data-testid="play-reload" onClick={() => send({ t: "reload" })} title="Reload game" className="w-9 h-9 rounded-lg border border-zinc-800 flex items-center justify-center text-zinc-400 hover:text-white"><RotateCw className="w-4 h-4" /></button>
            <button data-testid="play-fullscreen" onClick={toggleFs} title="Fullscreen" className="w-9 h-9 rounded-lg border border-zinc-800 flex items-center justify-center text-zinc-400 hover:text-white"><Maximize className="w-4 h-4" /></button>
            <button data-testid="play-exit" onClick={exit} className="h-9 px-3 rounded-lg bg-red-500/15 text-red-400 text-sm font-medium flex items-center gap-1.5 hover:bg-red-500/25"><LogOut className="w-4 h-4" /> <span className="hidden sm:inline">Exit</span></button>
          </div>
        )}
      </header>

      <div ref={stageRef} onPointerDown={() => audio.unlock()} className="relative flex-1 bg-black overflow-hidden flex items-center justify-center">
        <canvas ref={canvasRef} tabIndex={0} data-testid="play-canvas" width={info.size[0]} height={info.size[1]}
          className={`w-full h-full outline-none touch-none ${playing ? "" : "invisible"}`} style={{ objectFit: "contain" }} />
        {playing && pad && <VirtualPad send={send} />}
        {playing && !fs && <div data-play-ui className="portrait:flex hidden sm:!hidden absolute top-3 inset-x-0 justify-center pointer-events-none"><span data-testid="play-rotate-hint" className="text-xs bg-black/70 text-zinc-300 rounded-full px-3 py-1">Turn your phone sideways or tap fullscreen for a bigger picture</span></div>}
        {fs && <button data-play-ui data-testid="play-fullscreen-exit" onClick={toggleFs} className="absolute top-3 right-3 w-10 h-10 rounded-lg bg-black/60 border border-[#fff]/20 flex items-center justify-center text-[#fff]"><Minimize className="w-4 h-4" /></button>}
        {phase === "reconnecting" && <div data-testid="play-reconnecting" className="absolute top-3 left-1/2 -translate-x-1/2 bg-amber-500/90 text-black text-sm font-semibold rounded-full px-4 py-1.5 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Reconnecting…</div>}

        {err && <Center><div className="font-head text-xl font-semibold" data-testid="play-error">{err}</div><p className="text-zinc-400 text-sm">Run /game in Discord to get a fresh link.</p></Center>}
        {!err && !meta && <Center><Loader2 className="w-8 h-8 animate-spin text-volt" /></Center>}
        {!err && meta && phase === "idle" && (
          <Center>
            <div className="w-16 h-16 rounded-2xl bg-volt/15 flex items-center justify-center"><Gamepad2 className="w-8 h-8 text-volt" /></div>
            <div><h1 className="font-head text-2xl sm:text-3xl font-bold">{meta.title}</h1><p className="text-zinc-400 text-sm mt-1">Runs in a protected cloud browser. Pop-ups, ads and redirects are filtered. Nothing to install.</p></div>
            {meta.expired ? <p data-testid="play-expired" className="text-amber-400 text-sm">This link has expired. Run /game in Discord again.</p> : (<>
              <div className="flex gap-1 bg-zinc-900 rounded-lg p-1 text-sm" data-play-ui>
                {[["540", "Smooth 540p"], ["720", "HD 720p"], ...(meta.allow_1080 ? [["1080", "Full HD"]] : [])].map(([v, l]) => (
                  <button key={v} data-testid={`play-quality-${v}`} onClick={() => setQ(v)} className={`px-3 py-1 rounded-md ${q === v ? "bg-zinc-800 text-white" : "text-zinc-400"}`}>{l}</button>))}
              </div>
              <ControlsCard controls={meta.controls} touch={isTouch} />
              <button data-testid="play-start" onClick={() => start(q)} className="bg-volt text-volt-ink font-semibold rounded-lg px-8 py-3 hover:bg-volt-hover transition-colors">▶ Play now</button>
              <p className="text-xs text-zinc-500 max-w-sm">Sound plays for most games. Pick Smooth 540p for a higher frame rate on slower connections. Please don't enter passwords on third-party game sites.</p>
            </>)}
          </Center>
        )}
        {(phase === "starting" || phase === "queued") && (
          <Center>
            <Loader2 className="w-10 h-10 animate-spin text-volt" />
            <div data-testid="play-loading" className="font-head text-lg font-semibold">{phase === "queued" ? `Servers are busy — you're #${info.queue} in the queue` : "Starting your protected browser…"}</div>
            <p className="text-zinc-500 text-sm">Big games can take a minute to load the first time.</p>
          </Center>
        )}
        {phase === "ended" && (
          <Center>
            <div data-testid="play-ended" className="font-head text-xl font-semibold">Session ended</div>
            <p className="text-zinc-400 text-sm" data-testid="play-ended-reason">{info.reason}</p>
            <p className="text-zinc-500 text-xs">Run /game in Discord to play again.</p>
          </Center>
        )}
      </div>
    </div>
  );
}
